/**
 * Slug validation pattern matching the backend API requirements.
 * Allows lowercase letters, numbers, and hyphens (not at start/end).
 * Examples: "my-agency", "agency123", "my-cool-agency-2"
 */
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/**
 * Validates if a slug matches the required format.
 * @param slug - The slug string to validate
 * @returns true if the slug is valid, false otherwise
 */
export const isValidSlugFormat = (slug: string): boolean => {
  if (!slug || slug.length === 0) {
    return false
  }
  if (slug.length > 100) {
    return false
  }
  return SLUG_PATTERN.test(slug)
}

/**
 * Generates a URL-friendly slug from a given name.
 * Converts to lowercase, replaces spaces and special characters with hyphens,
 * removes consecutive hyphens, and trims hyphens from start/end.
 *
 * @param name - The name to convert to a slug
 * @returns A valid slug string
 *
 * @example
 * generateSlugFromName("My Cool Agency") // "my-cool-agency"
 * generateSlugFromName("Test Agency 123!") // "test-agency-123"
 * generateSlugFromName("  Multiple   Spaces  ") // "multiple-spaces"
 */
export const generateSlugFromName = (name: string): string => {
  if (!name) {
    return ''
  }

  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 100)
}

/**
 * Sanitizes a user-entered slug to ensure it matches the required format.
 * Similar to generateSlugFromName but preserves existing valid slug structure.
 *
 * @param slug - The slug to sanitize
 * @returns A sanitized slug string
 */
export const sanitizeSlug = (slug: string): string => {
  if (!slug) {
    return ''
  }

  return slug
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-/, '')
    .slice(0, 100)
}
